import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, ToggleButtonGroup, ToggleButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { api } from '../../utils/api';

type AccessAction = 'approve' | 'reject' | 'resend' | 'reset' | 'revoke' | 'delete';

// Actions that mutate/destroy state and require a confirm dialog first.
const DESTRUCTIVE: AccessAction[] = ['reset', 'revoke', 'delete'];

const SUCCESS_COPY: Record<AccessAction, string> = {
  approve: 'Request approved — approval email sent.',
  reject: 'Request rejected — applicant notified.',
  resend: 'Approval email re-sent.',
  reset: 'Request reset to pending.',
  revoke: 'Early access revoked.',
  delete: 'Request deleted.',
};

const AccessRequestsTab: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [confirm, setConfirm] = useState<{ requestId: string; action: AccessAction; name: string } | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/admin/users/access-requests?status=${filter}`);
      const data = await res.json();
      if (data.success) {
        setRequests(data.requests || []);
      } else {
        setError(data.error || 'Failed to load requests');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Performs the POST. Non-destructive actions call this directly; destructive
  // ones route through the confirm dialog first (see requestAction).
  const performAction = async (requestId: string, action: AccessAction) => {
    setProcessing(requestId);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/admin/users/access-requests', { requestId, action });
      const data = await res.json();
      if (data.success) {
        setSuccess(SUCCESS_COPY[action]);
        fetchRequests();
      } else {
        setError(data.error || `Failed to ${action}`);
      }
    } catch {
      setError(`Failed to ${action} request`);
    } finally {
      setProcessing(null);
    }
  };

  // Entry point for every button. Destructive actions open the confirm dialog;
  // everything else runs immediately.
  const requestAction = (requestId: string, action: AccessAction, name: string) => {
    if (DESTRUCTIVE.includes(action)) {
      setConfirm({ requestId, action, name });
    } else {
      performAction(requestId, action);
    }
  };

  const confirmCopy = (action: AccessAction, name: string): { title: string; body: string; cta: string } => {
    switch (action) {
      case 'reset':
        return {
          title: 'Reset to pending?',
          body: `This clears the decision on ${name}'s request and moves it back to the pending queue for re-evaluation.`,
          cta: 'Reset',
        };
      case 'revoke':
        return {
          title: 'Revoke early access?',
          body: `Revoke early access for ${name}. If they have already created an account, it will be suspended and they'll receive a revocation email; if they were approved but never signed up, their invitation is cancelled and they'll receive a cancellation email. The request will be marked rejected.`,
          cta: 'Revoke',
        };
      case 'delete':
        return {
          title: 'Delete request?',
          body: `Permanently delete ${name}'s early access request. This cannot be undone.`,
          cta: 'Delete',
        };
      default:
        return { title: 'Confirm', body: '', cta: 'Confirm' };
    }
  };

  const btnSx = {
    contained: { bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#1F4E4A' } },
    neutral: { textTransform: 'none', borderRadius: 2, color: '#2D6E6A', borderColor: '#2D6E6A' },
    danger: { textTransform: 'none', borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f' },
  } as const;

  const spin = (id: string) => processing === id ? <CircularProgress size={14} /> : undefined;

  // Renders the right set of buttons for a row based on its status.
  const renderActions = (req: any) => {
    const id = req.requestId;
    const name = req.name;
    const busy = processing !== null;

    const Approve = (
      <Button key="approve" size="small" variant="contained"
        startIcon={spin(id) || <CheckCircleIcon />} disabled={busy}
        onClick={() => requestAction(id, 'approve', name)} sx={btnSx.contained}>Approve</Button>
    );
    const Reject = (
      <Button key="reject" size="small" variant="outlined"
        startIcon={<CancelIcon />} disabled={busy}
        onClick={() => requestAction(id, 'reject', name)} sx={btnSx.danger}>Reject</Button>
    );
    const Resend = (
      <Button key="resend" size="small" variant="outlined"
        startIcon={spin(id) || <SendIcon />} disabled={busy}
        onClick={() => requestAction(id, 'resend', name)} sx={btnSx.neutral}>Resend</Button>
    );
    const Reset = (
      <Button key="reset" size="small" variant="outlined"
        startIcon={<ReplayIcon />} disabled={busy}
        onClick={() => requestAction(id, 'reset', name)} sx={btnSx.neutral}>Reset</Button>
    );
    const Revoke = (
      <Button key="revoke" size="small" variant="outlined"
        startIcon={<BlockIcon />} disabled={busy}
        onClick={() => requestAction(id, 'revoke', name)} sx={btnSx.danger}>Revoke</Button>
    );
    const Delete = (
      <Button key="delete" size="small" variant="outlined"
        startIcon={<DeleteOutlineIcon />} disabled={busy}
        onClick={() => requestAction(id, 'delete', name)} sx={btnSx.danger}>Delete</Button>
    );

    let buttons: React.ReactNode[] = [];
    if (req.status === 'pending') buttons = [Approve, Reject, Delete];
    else if (req.status === 'approved') buttons = [Resend, Reset, Revoke, Delete];
    else if (req.status === 'rejected') buttons = [Reset, Delete];

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {buttons}
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Early Access Requests</Typography>
        <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#2D6E6A' }} /></Box>
      ) : requests.length === 0 ? (
        <Alert severity="info">No {filter} access requests.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.requestId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{req.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{req.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {req.message || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{new Date(req.requestedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {renderActions(req)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        {confirm && (() => {
          const copy = confirmCopy(confirm.action, confirm.name);
          return (
            <>
              <DialogTitle sx={{ fontWeight: 600 }}>{copy.title}</DialogTitle>
              <DialogContent>
                <DialogContentText>{copy.body}</DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => { const c = confirm; setConfirm(null); performAction(c.requestId, c.action); }}
                  sx={{
                    textTransform: 'none', borderRadius: 2,
                    bgcolor: confirm.action === 'reset' ? '#2D6E6A' : '#d32f2f',
                    '&:hover': { bgcolor: confirm.action === 'reset' ? '#1F4E4A' : '#b71c1c' },
                  }}
                >
                  {copy.cta}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default AccessRequestsTab;
